import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, ArrowRight, Leaf, Sparkles, Award, Heart, Star, Instagram, ChevronRight } from 'lucide-react';
import { BRAND, IMG, PLATFORMS, WHY_US, TESTIMONIALS } from '../data/mock';
import { getMenu, getGallery, resolveImg } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import GalleryLightbox from '../components/GalleryLightbox';
import ReelsSection from '../components/ReelsSection';
import PlatformLogo from '../components/PlatformLogo';
import Seo from '../components/Seo';

const iconMap = { Leaf, Sparkles, Award, Heart };

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    getMenu().then(m => setBestsellers(m.filter(x => x.is_bestseller).slice(0, 6)));
    getGallery().then(setGallery);
  }, []);

  return (
    <div className="pt-20">
      <Seo title="" path="/" description="Zayka Sphere — Taste Ka Asli Global Station. Lucknow's crazy-good cloud kitchen for burgers, momos, pasta, shakes & street food. Order online or explore franchise opportunities." />
      {/* HERO */}
      <section className="relative overflow-hidden ember-bg grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-16 w-[28rem] h-[28rem] bg-orange-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-6">
              <Flame className="w-3.5 h-3.5" /> {BRAND.tagline}
            </div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-white">
              CRAZY FOOD.<br />
              <span className="text-orange-500 fire-glow">GLOBAL AMBITION.</span>
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-xl">Experience bold flavours from Zayka Sphere — your destination for crazy food, unforgettable bites and a growing global food brand.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/menu">
                <Button className="h-12 px-7 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-widest btn-pulse">
                  <Flame className="w-4 h-4 mr-2" /> Order Now
                </Button>
              </Link>
              <Link to="/franchise">
                <Button variant="outline" className="h-12 px-7 rounded-full border-orange-500/60 text-orange-400 hover:bg-orange-500 hover:text-white font-bold uppercase tracking-widest bg-transparent">
                  Explore Franchise <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-white/50">
              <span>• Fresh Ingredients</span>
              <span>• Hygienic Food</span>
              <span>• Best Quality</span>
              <span>• Made With Love</span>
            </div>
          </motion.div>
          <motion.div className="relative tilt-scene" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}>
            <div className="absolute -inset-6 bg-gradient-to-br from-orange-600/30 to-red-600/20 blur-3xl rounded-[3rem]" />
            <div className="tilt-card relative rounded-[2rem] overflow-hidden border border-orange-500/30 shadow-2xl shadow-orange-500/20 will-change-transform">
              <span className="tilt-glare" />
              <img src={IMG.burger} alt="burger" className="w-full h-[520px] object-cover img-fade" />
              <div className="absolute top-4 left-4 rotate-[-6deg] bg-white text-black font-marker text-sm px-3 py-1 rounded-md shadow-lg z-10">Crazy Food for Crazy You!</div>
              <div className="absolute bottom-4 right-4 bg-orange-500 text-white font-bebas text-2xl px-4 py-2 rounded-lg shadow-lg z-10">ORDER NOW</div>
            </div>
            <img src={BRAND.logo} alt="logo" className="hidden md:block absolute -top-8 -left-8 w-36 h-36 object-contain drop-shadow-[0_6px_18px_rgba(249,115,22,0.5)] floaty z-10" />
          </motion.div>
        </div>

        {/* marquee */}
        <div className="relative border-y border-orange-500/20 bg-black/60 overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-4">
            {Array.from({length: 2}).map((_, i) => (
              <div key={i} className="flex items-center gap-10 pr-10 font-display text-3xl tracking-widest text-white/80">
                <span>ZAYKA SPHERE</span><Flame className="w-6 h-6 text-orange-500" />
                <span className="text-orange-500">CRAZY FOOD</span><Flame className="w-6 h-6 text-orange-500" />
                <span>FOR CRAZY YOU</span><Flame className="w-6 h-6 text-orange-500" />
                <span className="text-orange-500">TASTE KA ASLI GLOBAL STATION</span><Flame className="w-6 h-6 text-orange-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORDER PLATFORMS */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Online Ordering</div>
            <h2 className="font-display text-4xl md:text-5xl">Your Favourite Food, <span className="text-orange-500">Just A Click Away</span></h2>
            <p className="mt-4 text-white/60">Order Zayka Sphere through your preferred delivery platform.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLATFORMS.map(p => (
              <a key={p.name} href={p.url} target="_blank" rel="noreferrer" className="group relative overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 p-6 flex items-center gap-4 hover:border-orange-500/50 transition">
                <PlatformLogo platform={p} size={56} />
                <div className="flex-1">
                  <div className="font-bebas text-2xl tracking-wide">{p.name}</div>
                  <div className="text-white/50 text-sm">Order on {p.name}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition" />
              </a>
            ))}
          </div>
          <p className="text-center text-xs text-white/40 mt-6">Live restaurant links — Rajajipuram, Lucknow.</p>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Why Us</div>
            <h2 className="font-display text-4xl md:text-5xl">Why People Love <span className="text-orange-500">Zayka Sphere</span></h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_US.map((w, i) => {
              const Icon = iconMap[w.icon];
              return (
                <Card key={i} className="food-card group bg-neutral-900 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-orange-500/10 border border-orange-500/40 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="mt-5 font-bebas text-2xl tracking-wide">{w.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{w.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-orange-500/20 to-red-500/10 blur-3xl rounded-[3rem]" />
            <img src={BRAND.banner1} alt="promo" className="relative rounded-3xl border border-orange-500/30 shadow-2xl w-full object-cover" />
          </div>
          <div>
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Experience</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">A Little Crazy. <span className="text-orange-500">A Lot Delicious.</span></h2>
            <p className="mt-5 text-white/70 leading-relaxed">From loaded burgers and crispy fries to flavour-packed momos, pasta, Maggi, pav bhaji, poha, refreshing mocktails and indulgent shakes — Zayka Sphere brings variety, flavour and fun together under one brand.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {['Burgers','Momos','Pasta','Fries','Shakes','Mocktails','Pav Bhaji','Maggi'].map(t => (
                <span key={t} className="px-3 py-1.5 rounded-full text-xs uppercase tracking-wider bg-neutral-900 border border-orange-500/30 text-orange-300">{t}</span>
              ))}
            </div>
            <Link to="/menu" className="inline-flex mt-8"><Button className="h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Explore Our Menu <ArrowRight className="w-4 h-4 ml-2"/></Button></Link>
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Bestsellers</div>
              <h2 className="font-display text-4xl md:text-5xl">What's Hot <span className="text-orange-500">At Zayka Sphere</span></h2>
              <p className="mt-3 text-white/60">Crowd favourites that keep people coming back for more.</p>
            </div>
            <Link to="/menu" className="text-orange-500 hover:text-orange-400 text-sm font-semibold uppercase tracking-widest inline-flex items-center gap-1">View Full Menu <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {bestsellers.map((b) => (
              <Card key={b.id} className="food-card group bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden p-0 will-change-transform">
                <div className="relative h-52 overflow-hidden">
                  <img src={resolveImg(b.image_url)} alt={b.name} className="w-full h-full object-cover img-fade" />
                  <div className="absolute top-3 left-3 bg-orange-500 text-white font-bebas text-xl px-3 py-1 rounded-lg shadow-lg z-10">₹{b.price}</div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                </div>
                <div className="p-5">
                  <h3 className="font-bebas text-2xl tracking-wide">{b.name}</h3>
                  <p className="mt-2 text-sm text-white/60">{b.desc}</p>
                  <Link to="/menu" className="mt-4 inline-flex items-center gap-1 text-orange-500 hover:text-orange-400 text-sm font-semibold uppercase tracking-widest">Order Now <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FRANCHISE CTA */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Franchise</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Become A Zayka Sphere <span className="text-orange-500">Franchise Partner</span></h2>
            <p className="mt-5 text-white/70">Own a food business powered by a bold brand, scalable systems and a global expansion vision.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/franchise"><Button className="h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Apply For Franchise</Button></Link>
              <Link to="/global-expansion"><Button variant="outline" className="h-12 px-6 rounded-full border-orange-500/60 text-orange-400 hover:bg-orange-500 hover:text-white font-bold uppercase tracking-widest bg-transparent">Global Expansion</Button></Link>
            </div>
          </div>
          <div className="relative">
            <img src={IMG.interior} alt="franchise" className="rounded-3xl border border-orange-500/30 shadow-2xl w-full h-[380px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-black border border-orange-500/40 rounded-2xl p-4 shadow-2xl">
              <div className="font-bebas text-3xl text-orange-500">3 Formats</div>
              <div className="text-white/60 text-xs uppercase tracking-widest">Express • Dine-In • Lounge</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Social Proof</div>
            <h2 className="font-display text-4xl md:text-5xl">The Zayka Sphere <span className="text-orange-500">Experience</span></h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Card key={i} className="food-card bg-neutral-900 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-1 text-orange-500 mb-3">
                  {Array.from({length:5}).map((_,k)=>(<Star key={k} className="w-4 h-4 fill-orange-500" />))}
                </div>
                <p className="text-white/80 leading-relaxed">“{t.quote}”</p>
                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-white/50 uppercase tracking-widest">{t.role}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* REELS */}
      <ReelsSection />

      {/* INSTAGRAM / GALLERY */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">{BRAND.ig_handle}</div>
            <h2 className="font-display text-4xl md:text-5xl">Follow The <span className="text-orange-500">Craze</span></h2>
            <p className="mt-3 text-white/60">Tap any photo to open the full-screen viewer.</p>
          </div>
          <GalleryLightbox
            images={gallery.map(g => resolveImg(g.image_url))}
            captions={gallery.map(g => g.caption)}
          />
          <div className="text-center mt-8">
            <a href={BRAND.instagram} target="_blank" rel="noreferrer">
              <Button className="h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Instagram className="w-4 h-4 mr-2" /> Follow Us On Instagram</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
